import React, { useState, useMemo } from 'react';
import { ClipboardCheck, CheckSquare, Square, ClipboardList, Truck, PackageCheck, ArrowRight, AlertTriangle, Clock } from "lucide-react";

const DEFAULT_ITEMS = ["LPG 20kg", "LPG 50kg", "산소(O2)", "질소(N2)", "아르곤(Ar)"];
const ITEM_COLORS = {
  "LPG 20kg":   { bg: "#e8f0fb", border: "#185FA5", text: "#185FA5" },
  "LPG 50kg":   { bg: "#ede9fe", border: "#7C3AED", text: "#7C3AED" },
  "산소(O2)":   { bg: "#e8faf2", border: "#059669", text: "#059669" },
  "질소(N2)":   { bg: "#fff7ed", border: "#D97706", text: "#D97706" },
  "아르곤(Ar)": { bg: "#fef2f2", border: "#DC2626", text: "#DC2626" },
  "탄산(CO2)":  { bg: "#f0fdf4", border: "#16A34A", text: "#16A34A" },
};

const today = new Date().toISOString().split('T')[0];

const getExpiryStatus = (expiry) => {
  if (!expiry) return null;
  const daysLeft = Math.ceil((new Date(expiry) - new Date()) / 86400000);
  if (daysLeft < 0)  return { label: '만료', color: '#DC2626', bg: '#fef2f2', days: daysLeft };
  if (daysLeft <= 30) return { label: `${daysLeft}일 남음`, color: '#D97706', bg: '#fff7ed', days: daysLeft };
  return null;
};

const InspectionPanel = ({ cylinders, setCylinders, items = [], warehouses = [], inspectionHistory = [], setInspectionHistory, setCollections }) => {
  const gasItems = items.length > 0 ? items.map(i => i.name) : DEFAULT_ITEMS;

  const [subTab,       setSubTab]       = useState('out');
  const [inspCompany,  setInspCompany]  = useState('');
  const [inspDate,     setInspDate]     = useState(today);
  const [newExpiry,    setNewExpiry]    = useState('');
  const [filterItem,   setFilterItem]   = useState('전체');
  const [expiredOnly,  setExpiredOnly]  = useState(false);
  const [selectedOut,  setSelectedOut]  = useState([]);
  const [selectedIn,   setSelectedIn]   = useState([]);
  const [inWarehouse,  setInWarehouse]  = useState('');
  const [inspCost,     setInspCost]     = useState('');
  const [done,         setDone]         = useState('');

  const warehouseList = useMemo(() =>
    [...new Set(cylinders.filter(c => c.locType === 'warehouse').map(c => c.location))],
  [cylinders]);

  const effectiveWarehouse = inWarehouse || warehouseList[0] || '제1 창고(메인)';

  // 요약 집계
  const inInspection  = useMemo(() => cylinders.filter(c => c.locType === 'inspection'), [cylinders]);
  const expiredCount  = useMemo(() => cylinders.filter(c =>
    c.locType === 'warehouse' && c.inspectionExpiry && c.inspectionExpiry < today
  ).length, [cylinders]);
  const nearCount     = useMemo(() => cylinders.filter(c => {
    if (!c.inspectionExpiry) return false;
    const d = Math.ceil((new Date(c.inspectionExpiry) - new Date()) / 86400000);
    return d >= 0 && d <= 30;
  }).length, [cylinders]);

  // 검사 출고 후보: 창고 공병
  const outCandidates = useMemo(() =>
    cylinders.filter(c =>
      c.locType === 'warehouse' && c.status === '공병' &&
      (filterItem === '전체' || c.item === filterItem) &&
      (!expiredOnly || (c.inspectionExpiry && c.inspectionExpiry <= today))
    ),
  [cylinders, filterItem, expiredOnly]);

  // ── 검사 출고 처리 ─────────────────────────────────────────
  const handleOut = () => {
    if (selectedOut.length === 0 || !inspCompany.trim()) return;
    const targets = cylinders.filter(c => selectedOut.includes(c.id));
    const summary = targets.reduce((acc, c) => { acc[c.item] = (acc[c.item] || 0) + 1; return acc; }, {});
    setCylinders(prev => prev.map(c =>
      selectedOut.includes(c.id)
        ? { ...c, locType: 'inspection', location: inspCompany.trim(), sentToInspectionAt: inspDate }
        : c
    ));
    setInspectionHistory(prev => [{
      type: 'out', date: inspDate, company: inspCompany.trim(),
      count: targets.length, summary, ids: [...selectedOut]
    }, ...prev]);
    setSelectedOut([]);
    setDone('출고');
    setTimeout(() => setDone(''), 2500);
  };

  // ── 검사 완료 입고 처리 ────────────────────────────────────
  const handleIn = () => {
    if (selectedIn.length === 0) return;
    const targets = cylinders.filter(c => selectedIn.includes(c.id));
    const companyName = targets[0]?.location || '검사업체';
    const summary = targets.reduce((acc, c) => { acc[c.item] = (acc[c.item] || 0) + 1; return acc; }, {});
    const cost = parseInt(inspCost) || 0;
    setCylinders(prev => prev.map(c =>
      selectedIn.includes(c.id)
        ? {
            ...c,
            locType: 'warehouse',
            location: effectiveWarehouse,
            status: '공병',
            ...(newExpiry ? { inspectionExpiry: newExpiry } : {}),
            inspectionCompletedAt: inspDate,
          }
        : c
    ));
    setInspectionHistory(prev => [{
      type: 'in', date: inspDate,
      company: companyName,
      count: targets.length, summary, ids: [...selectedIn],
      warehouse: effectiveWarehouse,
      newExpiry: newExpiry || '',
      cost,
    }, ...prev]);
    if (cost > 0 && setCollections) {
      setCollections(prev => [...prev, {
        id: Date.now(),
        type: 'service',
        customer: companyName,
        item: '용기검사 서비스',
        items: { '용기검사 서비스': targets.length },
        quantity: targets.length,
        price: Math.round(cost / targets.length),
        amount: cost,
        date: inspDate,
        targetWarehouse: '',
        cylinders: [],
      }]);
    }
    setSelectedIn([]);
    setNewExpiry('');
    setInspCost('');
    setDone('입고');
    setTimeout(() => setDone(''), 2500);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* 상단 요약 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
        <SummaryCard label="검사 중인 용기"   value={`${inInspection.length}개`} color="#7C3AED" />
        <SummaryCard label="검사기한 만료"    value={`${expiredCount}개`}        color="#DC2626" />
        <SummaryCard label="30일 이내 만료"   value={`${nearCount}개`}           color="#D97706" />
      </div>

      {/* 서브탭 */}
      <div style={{ display: 'flex', gap: 0, background: 'white', border: '1px solid #d3d1c7', borderRadius: 12, overflow: 'hidden' }}>
        <button onClick={() => setSubTab('out')}
          style={{ flex: 1, padding: '14px 0', border: 'none',
            background: subTab === 'out' ? '#fff7ed' : 'white',
            color: subTab === 'out' ? '#D97706' : '#888',
            fontWeight: subTab === 'out' ? 700 : 400, fontSize: 14, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            borderBottom: subTab === 'out' ? '3px solid #D97706' : '3px solid transparent' }}>
          <Truck size={16} /> 검사 출고 (공병 → 검사업체)
        </button>
        <button onClick={() => setSubTab('in')}
          style={{ flex: 1, padding: '14px 0', border: 'none',
            background: subTab === 'in' ? '#f0fdf8' : 'white',
            color: subTab === 'in' ? '#059669' : '#888',
            fontWeight: subTab === 'in' ? 700 : 400, fontSize: 14, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            borderBottom: subTab === 'in' ? '3px solid #059669' : '3px solid transparent' }}>
          <PackageCheck size={16} /> 검사 완료 입고
          {inInspection.length > 0 && (
            <span style={{ background: '#059669', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px' }}>
              {inInspection.length}
            </span>
          )}
        </button>
      </div>

      {/* ── 검사 출고 ── */}
      {subTab === 'out' && (
        <div style={sectionBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Truck size={20} color="#D97706" />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>공병 → 검사업체 출고</h3>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={miniLabel}>검사업체명</label>
              <input
                style={inputStyle}
                placeholder="예: 한국가스안전공사"
                value={inspCompany}
                onChange={e => setInspCompany(e.target.value)}
              />
            </div>
            <div>
              <label style={miniLabel}>출고일</label>
              <input type="date" style={inputStyle} value={inspDate} onChange={e => setInspDate(e.target.value)} />
            </div>
          </div>

          {/* 품목 필터 + 만료 토글 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['전체', ...gasItems].map(item => (
                <button key={item} onClick={() => setFilterItem(item)}
                  style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                    background: filterItem === item ? '#D97706' : '#f0f0f0',
                    color: filterItem === item ? '#fff' : '#666' }}>
                  {item}
                </button>
              ))}
            </div>
            <button onClick={() => setExpiredOnly(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                background: expiredOnly ? '#fef2f2' : '#f0f0f0',
                color: expiredOnly ? '#DC2626' : '#666' }}>
              <AlertTriangle size={13} />
              {expiredOnly ? '만료 용기만 보기' : '전체 보기'}
            </button>
          </div>

          {/* 용기 목록 */}
          {outCandidates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#bbb', fontSize: 14, border: '1px dashed #ddd', borderRadius: 10 }}>
              {expiredOnly ? '검사기한이 만료된 공병이 없습니다' : '창고에 공병이 없습니다'}
            </div>
          ) : (
            <div style={{ border: '1px solid #e5e3db', borderRadius: 10, overflow: 'hidden' }}>
              {/* 전체 선택 헤더 */}
              <div onClick={() => {
                const allSel = outCandidates.every(c => selectedOut.includes(c.id));
                if (allSel) setSelectedOut(prev => prev.filter(id => !outCandidates.find(c => c.id === id)));
                else setSelectedOut(prev => [...new Set([...prev, ...outCandidates.map(c => c.id)])]);
              }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: '#f8f7f2', borderBottom: '1px solid #e5e3db', cursor: 'pointer', userSelect: 'none' }}>
                {outCandidates.every(c => selectedOut.includes(c.id)) && outCandidates.length > 0
                  ? <CheckSquare size={18} color="#185FA5" />
                  : <Square size={18} color="#aaa" />}
                <span style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>전체 선택 ({outCandidates.length}개)</span>
                {selectedOut.filter(id => outCandidates.find(c => c.id === id)).length > 0 && (
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: '#185FA5', fontWeight: 700 }}>
                    {selectedOut.filter(id => outCandidates.find(c => c.id === id)).length}개 선택됨
                  </span>
                )}
              </div>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {outCandidates.map(c => {
                  const checked = selectedOut.includes(c.id);
                  const col = ITEM_COLORS[c.item];
                  const expStatus = getExpiryStatus(c.inspectionExpiry);
                  return (
                    <div key={c.id}
                      onClick={() => setSelectedOut(prev => checked ? prev.filter(x => x !== c.id) : [...prev, c.id])}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                        borderBottom: '1px solid #f5f5f5', cursor: 'pointer',
                        background: checked ? '#f0f6ff' : (expStatus?.days < 0 ? '#fff8f8' : '#fff'),
                        userSelect: 'none' }}>
                      {checked ? <CheckSquare size={17} color="#185FA5" /> : <Square size={17} color="#ccc" />}
                      <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#333', minWidth: 100 }}>{c.id}</span>
                      {c.item && (
                        <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                          background: col ? col.bg : '#e8f0fb', color: col ? col.text : '#185FA5' }}>
                          {c.item}
                        </span>
                      )}
                      {expStatus ? (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                          background: expStatus.bg, color: expStatus.color, display: 'flex', alignItems: 'center', gap: 4 }}>
                          {expStatus.days < 0 ? <AlertTriangle size={11} /> : <Clock size={11} />}
                          검사기한 {expStatus.label} ({c.inspectionExpiry})
                        </span>
                      ) : c.inspectionExpiry ? (
                        <span style={{ fontSize: 11, color: '#aaa' }}>기한: {c.inspectionExpiry}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <button onClick={handleOut}
              disabled={selectedOut.length === 0 || !inspCompany.trim()}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '11px 28px',
                background: (selectedOut.length === 0 || !inspCompany.trim()) ? '#e5e3db' : '#D97706',
                color: (selectedOut.length === 0 || !inspCompany.trim()) ? '#aaa' : '#fff',
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14,
                cursor: (selectedOut.length === 0 || !inspCompany.trim()) ? 'default' : 'pointer' }}>
              <ArrowRight size={16} /> 검사업체로 출고 ({selectedOut.length}개)
            </button>
            {done === '출고' && <span style={{ fontSize: 13, color: '#D97706', fontWeight: 700 }}>✓ 출고 처리 완료!</span>}
          </div>
          {inspCompany.trim() === '' && selectedOut.length > 0 && (
            <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#DC2626', fontWeight: 600 }}>검사업체명을 입력하세요.</p>
          )}
        </div>
      )}

      {/* ── 검사 완료 입고 ── */}
      {subTab === 'in' && (
        <div style={sectionBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <PackageCheck size={20} color="#059669" />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>검사 완료 → 공병 입고</h3>
          </div>

          {inInspection.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#bbb', fontSize: 14, border: '1px dashed #e5e3db', borderRadius: 10 }}>
              검사업체에 맡긴 용기가 없습니다.<br />
              <span style={{ fontSize: 12 }}>먼저 "검사 출고"를 진행하세요.</span>
            </div>
          ) : (
            <>
              {/* 검사업체별 그룹 */}
              {[...new Set(inInspection.map(c => c.location))].map(company => {
                const group = inInspection.filter(c => c.location === company);
                return (
                  <div key={company} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ClipboardCheck size={13} /> {company} ({group.length}개)
                    </div>
                    <div style={{ border: '1px solid #e5e3db', borderRadius: 10, overflow: 'hidden' }}>
                      {group.map(c => {
                        const checked = selectedIn.includes(c.id);
                        const col = ITEM_COLORS[c.item];
                        return (
                          <div key={c.id}
                            onClick={() => setSelectedIn(prev => checked ? prev.filter(x => x !== c.id) : [...prev, c.id])}
                            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                              borderBottom: '1px solid #f5f5f5', cursor: 'pointer',
                              background: checked ? '#f0fdf8' : '#fff', userSelect: 'none' }}>
                            {checked ? <CheckSquare size={17} color="#059669" /> : <Square size={17} color="#ccc" />}
                            <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, flex: 1, color: '#333' }}>{c.id}</span>
                            {c.item && (
                              <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                                background: col ? col.bg : '#e8f0fb', color: col ? col.text : '#185FA5' }}>
                                {c.item}
                              </span>
                            )}
                            {c.sentToInspectionAt && (
                              <span style={{ fontSize: 11, color: '#aaa' }}>출고일: {c.sentToInspectionAt}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* 입고 설정 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                <div>
                  <label style={miniLabel}>입고 창고</label>
                  <select style={inputStyle} value={effectiveWarehouse} onChange={e => setInWarehouse(e.target.value)}>
                    {(warehouseList.length > 0 ? warehouseList : ['제1 창고(메인)']).map(w => (
                      <option key={w}>{w}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={miniLabel}>입고일</label>
                  <input type="date" style={inputStyle} value={inspDate} onChange={e => setInspDate(e.target.value)} />
                </div>
                <div>
                  <label style={miniLabel}>새 검사기한 (선택)</label>
                  <input type="date" style={inputStyle} value={newExpiry} onChange={e => setNewExpiry(e.target.value)} />
                </div>
                <div>
                  <label style={miniLabel}>검사 비용 총액 (원)</label>
                  <input type="number" style={inputStyle} placeholder="0 (없으면 생략)"
                    value={inspCost} onChange={e => setInspCost(e.target.value)} min={0} />
                </div>
              </div>
              {newExpiry && (
                <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#059669', fontWeight: 600 }}>
                  ✓ 선택한 용기의 검사기한이 {newExpiry}로 갱신됩니다.
                </p>
              )}
              {parseInt(inspCost) > 0 && (
                <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#7C3AED', fontWeight: 600 }}>
                  ✓ 검사 비용 {parseInt(inspCost).toLocaleString()}원이 매입 내역에 자동 등록됩니다.
                </p>
              )}

              <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 14 }}>
                <button onClick={handleIn}
                  disabled={selectedIn.length === 0}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '11px 28px',
                    background: selectedIn.length === 0 ? '#e5e3db' : '#059669',
                    color: selectedIn.length === 0 ? '#aaa' : '#fff',
                    border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14,
                    cursor: selectedIn.length === 0 ? 'default' : 'pointer' }}>
                  <PackageCheck size={16} /> 검사 완료 입고 ({selectedIn.length}개)
                </button>
                {done === '입고' && <span style={{ fontSize: 13, color: '#059669', fontWeight: 700 }}>✓ 입고 처리 완료!</span>}
              </div>
            </>
          )}
        </div>
      )}

      {/* 검사 이력 */}
      {inspectionHistory.length > 0 && (
        <div style={sectionBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <ClipboardList size={18} color="#7C3AED" />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>검사 이력</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {inspectionHistory.map((h, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10,
                border: '1px solid #e5e3db', background: h.type === 'out' ? '#fff7ed' : '#f0fdf8', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 12,
                  background: h.type === 'out' ? '#D97706' : '#059669', color: '#fff' }}>
                  {h.type === 'out' ? '출고' : '입고'}
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#333', minWidth: 90 }}>{h.date}</span>
                <span style={{ fontSize: 13, color: '#7C3AED', fontWeight: 700 }}>{h.company}</span>
                <span style={{ fontSize: 13, color: h.type === 'out' ? '#D97706' : '#059669', fontWeight: 700 }}>
                  총 {h.count}개 {h.type === 'out' ? '검사 출고' : '검사 완료 입고'}
                </span>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
                  {Object.entries(h.summary).map(([item, cnt]) => {
                    const col = ITEM_COLORS[item] || { bg: '#e8f0fb', text: '#185FA5' };
                    return (
                      <span key={item} style={{ fontSize: 12, background: col.bg, color: col.text, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                        {item} {cnt}개
                      </span>
                    );
                  })}
                </div>
                {h.warehouse && <span style={{ fontSize: 12, color: '#888' }}>→ {h.warehouse}</span>}
                {h.newExpiry && <span style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>검사기한: {h.newExpiry}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, color }) => (
  <div style={{ background: 'white', padding: '18px 20px', borderRadius: 14, border: '1px solid #d3d1c7' }}>
    <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
  </div>
);

const sectionBox = { background: 'white', padding: 24, borderRadius: 16, border: '1px solid #d3d1c7' };
const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #d3d1c7', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', background: '#fcfbf7' };
const miniLabel  = { display: 'block', fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 6 };

export default InspectionPanel;
